import { AutoComplete, DatePicker, Select, Switch } from 'antd'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useDebouncedState } from '../../hooks/useDebouncedState'
import { useSavedState } from '../../hooks/useSavedState'
import { crewMemberOptions, openProjectOptions, pushRecentSearch, TimesheetsFilters } from './timesheetsModel'
import { useOpenProjectsQuery, useTimesheetFilterOptionsQuery } from './timesheetsQueries'

const { RangePicker } = DatePicker

type Props = {
  filters: TimesheetsFilters
  onFiltersChange: (changes: Partial<TimesheetsFilters>) => void
}

const switchGroupStyle = { display: 'flex', alignItems: 'center', gap: 8 }

const TimesheetsFilterBar = ({ filters, onFiltersChange }: Props) => {
  const filterOptionsQuery = useTimesheetFilterOptionsQuery()
  const search = useDebouncedState(filters.search ?? '', 750)
  const projectSearch = useDebouncedState('', 500)
  const openProjectsQuery = useOpenProjectsQuery(projectSearch.debouncedState)
  const [recentSearches, setRecentSearches] = useSavedState<string[]>('timesheets-recent-searches', [])

  useEffect(() => {
    if (search.debouncedState !== (filters.search ?? '')) {
      onFiltersChange({ search: search.debouncedState || undefined })
      setRecentSearches(pushRecentSearch(recentSearches, search.debouncedState))
    }
  }, [search.debouncedState])

  return <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 16 }}>
    <RangePicker
      placeholder={['Start date', 'End date']}
      allowEmpty={[true, true]}
      value={filters.starting || filters.ending
        ? [filters.starting ? dayjs(filters.starting) : null, filters.ending ? dayjs(filters.ending) : null]
        : null
      }
      onChange={dates => onFiltersChange({
        starting: dates?.[0]?.format('YYYY-MM-DD'),
        ending: dates?.[1]?.format('YYYY-MM-DD'),
      })}
    />

    <Select
      value={filters.crewId ?? undefined}
      onChange={crewId => onFiltersChange({ crewId: crewId ?? null })}
      options={crewMemberOptions(filterOptionsQuery.data?.crewMembers ?? {})}
      placeholder="All crew members"
      allowClear
      style={{ minWidth: 180 }}
    />

    <Select
      value={filters.projectId ?? undefined}
      onChange={projectId => onFiltersChange({ projectId: projectId ?? null })}
      onSearch={projectSearch.setState}
      options={openProjectOptions(openProjectsQuery.data ?? [])}
      loading={openProjectsQuery.isFetching}
      notFoundContent={openProjectsQuery.isFetching ? 'Searching…' : 'No matching projects'}
      placeholder="search open customer projects..."
      showSearch
      filterOption={false}
      allowClear
      style={{ minWidth: 240 }}
    />

    <AutoComplete
      value={search.state}
      onChange={search.setState}
      options={recentSearches.map(value => ({ value }))}
      filterOption={(input, option) => (option?.value ?? '').toLowerCase().includes(input.toLowerCase())}
      placeholder="search timesheet entries..."
      allowClear
      style={{ width: 220 }}
    />

    <div style={switchGroupStyle}>
      <Switch
        checked={!!filters.includeUnsubmitted}
        onChange={includeUnsubmitted => onFiltersChange({ includeUnsubmitted })}
      />
      <span>Include Unsubmitted</span>
    </div>

    <div style={switchGroupStyle}>
      <Switch
        checked={!!filters.includeExported}
        onChange={includeExported => onFiltersChange({ includeExported })}
      />
      <span>Include Exported</span>
    </div>
  </div>
}

export default TimesheetsFilterBar
